/**
 * Shared games view for a single player. Used by:
 *   - /admin/players/:id  (Games tab)
 *   - /parent/games       (per-child)
 *
 * Renders the filter bar, played/total counts, and the games table.
 * Tournament links resolve to the role-appropriate page via the
 * optional `tournamentLink` prop.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Activity, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDateOnly } from '../utils/timezone';
import api from '../services/api';

const DEFAULT_FILTERS = {
  tournament: 'all',
  category: 'all',
  rsvp: 'all',
  selected: 'all',
  age: 'all',
  team: 'all',
  result: 'all',
  dateFrom: '',
  dateTo: '',
};

export const PlayerGamesView = ({ playerId, tournamentLink = null, title = 'Games' }) => {
  const [games, setGames] = useState([]);
  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    let cancelled = false;
    const fetchGames = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/players/${playerId}/games`);
        if (!cancelled) {
          setGames(res.data?.games || []);
          setGamesPlayed(res.data?.games_played || 0);
        }
      } catch (e) {
        console.error('Failed to load player games', e);
        if (!cancelled) setGames([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchGames();
    return () => { cancelled = true; };
  }, [playerId]);

  const filterOptions = (() => {
    const tournaments = new Set();
    const categories = new Set();
    const ages = new Set();
    const teams = new Set();
    const results = new Set();
    games.forEach(g => {
      if (g.tournament_name) tournaments.add(g.tournament_name);
      if (g.game_category) categories.add(g.game_category);
      else if (g.is_internal) categories.add('internal');
      if (g.age_category) ages.add(g.age_category);
      if (g.team_name) teams.add(g.team_name);
      if (g.result) results.add(String(g.result).toLowerCase());
    });
    return {
      tournaments: Array.from(tournaments).sort(),
      categories: Array.from(categories).sort(),
      ages: Array.from(ages).sort(),
      teams: Array.from(teams).sort(),
      results: Array.from(results).sort(),
    };
  })();

  const filteredGames = games.filter(g => {
    if (filters.tournament !== 'all' && g.tournament_name !== filters.tournament) return false;
    if (filters.category !== 'all') {
      const cat = g.game_category || (g.is_internal ? 'internal' : '');
      if (cat !== filters.category) return false;
    }
    if (filters.rsvp !== 'all') {
      const r = g.rsvp_response || 'none';
      if (r !== filters.rsvp) return false;
    }
    if (filters.selected !== 'all') {
      const want = filters.selected === 'yes';
      // iter339 — "Selected" == picked by coach, independent of RSVP
      // (matches the accounting export). RSVP=No does not unselect.
      const eff = Boolean(g.is_selected);
      if (eff !== want) return false;
    }
    if (filters.age !== 'all' && g.age_category !== filters.age) return false;
    if (filters.team !== 'all' && g.team_name !== filters.team) return false;
    if (filters.result !== 'all') {
      const r = (g.result ? String(g.result).toLowerCase() : '');
      if (r !== filters.result) return false;
    }
    if (filters.dateFrom && g.game_date && g.game_date < filters.dateFrom) return false;
    if (filters.dateTo && g.game_date && g.game_date > filters.dateTo) return false;
    return true;
  });

  const resetFilters = () => setFilters(DEFAULT_FILTERS);
  const hasActiveFilters = Object.entries(filters).some(([, v]) => v && v !== 'all');

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </CardContent>
      </Card>
    );
  }

  if (!games.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500" data-testid="player-games-empty">
            <Activity className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No games yet</p>
            <p className="text-sm mt-2">Games will appear here once the player is RSVP'd, rostered, or selected by a coach.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const resultBadge = (result) => {
    if (!result) return <span className="text-slate-400">—</span>;
    const norm = String(result).toLowerCase();
    const cls = norm === 'won' || norm === 'win'
      ? 'bg-green-100 text-green-700'
      : norm === 'lost' || norm === 'loss'
      ? 'bg-rose-100 text-rose-700'
      : norm === 'tie' || norm === 'draw'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-slate-100 text-slate-700';
    return <Badge className={`${cls} capitalize`}>{norm}</Badge>;
  };

  const rsvpBadge = (resp) => {
    if (!resp) return <span className="text-slate-400 text-xs">No RSVP</span>;
    const cls = resp === 'yes'
      ? 'bg-green-100 text-green-700'
      : resp === 'no'
      ? 'bg-rose-100 text-rose-700'
      : resp === 'tentative'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-slate-100 text-slate-700';
    return <Badge variant="outline" className={`${cls} capitalize text-xs`}>{resp}</Badge>;
  };

  const categoryBadge = (cat, isInternal) => {
    const label = isInternal ? 'Internal' : (cat ? cat.replace('_', ' ') : '—');
    if (!cat && !isInternal) return <span className="text-slate-400">—</span>;
    const norm = cat || (isInternal ? 'internal' : '');
    const cls = norm === 'practice' || norm === 'internal'
      ? 'bg-slate-100 text-slate-600'
      : norm === 'final' || norm === 'semi_final' || norm === 'quarter_final'
      ? 'bg-purple-100 text-purple-700'
      : norm === 'league'
      ? 'bg-blue-100 text-blue-700'
      : norm === 'competitive'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-emerald-100 text-emerald-700';
    return <Badge className={`${cls} capitalize text-xs`}>{label}</Badge>;
  };

  return (
    <Card data-testid="player-games-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Activity className="h-5 w-5" />
          {title}
          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700" data-testid="player-games-total">{games.length} total</Badge>
          <Badge className="bg-green-100 text-green-700" data-testid="player-games-played">{gamesPlayed} played</Badge>
          {hasActiveFilters && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700">{filteredGames.length} match filter</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">Includes games this player was selected for, RSVP'd to, or rostered on. "Played" = selected for a non-practice game.</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-4 p-3 bg-slate-50 rounded-lg" data-testid="player-games-filters">
          <Select value={filters.tournament} onValueChange={(v) => setFilters(f => ({ ...f, tournament: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-tournament"><SelectValue placeholder="Tournament" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tournaments</SelectItem>
              {filterOptions.tournaments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {filterOptions.categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.rsvp} onValueChange={(v) => setFilters(f => ({ ...f, rsvp: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-rsvp"><SelectValue placeholder="RSVP" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All RSVPs</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="none">No Response</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.selected} onValueChange={(v) => setFilters(f => ({ ...f, selected: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-selected"><SelectValue placeholder="Selected" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Selected: All</SelectItem>
              <SelectItem value="yes">Selected: Yes</SelectItem>
              <SelectItem value="no">Selected: No</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.age} onValueChange={(v) => setFilters(f => ({ ...f, age: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-age"><SelectValue placeholder="Age" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {filterOptions.ages.map(a => <SelectItem key={a} value={a} className="uppercase">{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.team} onValueChange={(v) => setFilters(f => ({ ...f, team: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-team"><SelectValue placeholder="Team" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {filterOptions.teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.result} onValueChange={(v) => setFilters(f => ({ ...f, result: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-result"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              {filterOptions.results.map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
            className="h-9 text-xs"
            data-testid="filter-date-from"
          />
          <Input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
            className="h-9 text-xs"
            data-testid="filter-date-to"
          />
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={resetFilters} className="h-9 text-xs" data-testid="filter-reset">
              <X className="h-3 w-3 mr-1" /> Reset
            </Button>
          )}
        </div>

        {filteredGames.length === 0 ? (
          <div className="text-center py-6 text-slate-500 text-sm">No games match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Tournament</TableHead>
                  <TableHead>Game</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>RSVP</TableHead>
                  <TableHead>Selected</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGames.map((g) => {
                  const tName = (
                    <span className="font-medium text-slate-800">{g.tournament_name}</span>
                  );
                  return (
                    <TableRow key={g.game_id} data-testid={`player-game-row-${g.game_id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div className="font-medium">{g.game_date ? formatDateOnly(g.game_date) : '—'}</div>
                        {g.start_time && <div className="text-xs text-slate-500">{g.start_time}</div>}
                      </TableCell>
                      <TableCell>
                        {tournamentLink ? (
                          <Link
                            to={tournamentLink(g.tournament_id)}
                            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            {g.tournament_name}
                          </Link>
                        ) : tName}
                        {g.tournament_year && <div className="text-xs text-slate-500">{g.tournament_year}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{g.game_name}</div>
                        {g.age_category && (
                          <Badge variant="outline" className="uppercase text-xs mt-1">{g.age_category}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{categoryBadge(g.game_category, g.is_internal)}</TableCell>
                      <TableCell className="text-sm">{g.team_name || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-sm">
                        {g.location_url ? (
                          <a href={g.location_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                            {g.location || 'Map'}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          g.location || <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>{rsvpBadge(g.rsvp_response)}</TableCell>
                      <TableCell>
                        {(() => {
                          // iter339 — Selected == picked, independent of RSVP.
                          const picked = Boolean(g.is_selected);
                          if (!picked) {
                            return <Badge variant="outline" className="bg-slate-100 text-slate-500 text-xs">No</Badge>;
                          }
                          // Picked but later RSVP'd No — still counts as
                          // selected; flag it so coaches can review.
                          const declined = g.rsvp_response === 'no';
                          return (
                            <Badge
                              className="bg-green-100 text-green-700 text-xs"
                              title={declined ? "Selected — player RSVP'd Unavailable" : 'Selected'}
                            >
                              Yes{declined ? ' *' : ''}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{resultBadge(g.result)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlayerGamesView;
